import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import * as _ from "lodash";
import { first } from "rxjs/operators";
import { CameraComponent } from "src/app/annotation/camera/camera.component";
import { TokenService } from "src/app/auth/token.service";
import { InsideSurvey } from "src/app/shared/Models/read";
import { InsideSurveyService } from "src/app/shared/services/inside-survey.service";
import { NavAction } from "src/app/shared/top-nav/top-nav.component";
import { SurveyStatus } from "../../../../../shared/Models/enums/SurveySurveyStatus";
import { ToastService } from "../../../../../shared/toast.service";
import { DeviceCheckService } from "../../../shared/services/device-check.service";
import { SavingStateService } from "../../../shared/services/saving-state.service";
import * as magicStrings from "../../config/inside-survey.magic-strings";
import { SurveyAbstractComponent } from "../../../shared/components/survey-abstract/survey-abstract.component";
import { FormValidationService } from "../../../shared/services/formValidation.service";
import { SessionService } from "../../../shared/services/session.service";
import { BuildingService } from "src/app/core/building-list/services/building.service";
import { ValidationError } from "src/app/shared/Models/ValidationError";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";

const RESULTS = "_results";

@Component({
  selector: "app-inside-survey",
  templateUrl: "./inside-survey.component.html",
  styleUrls: ["./inside-survey.component.scss"],
})
export class InsideSurveyComponent
  extends SurveyAbstractComponent
  implements OnInit, AfterViewInit
{
  @ViewChild("nav") protected tabs;
  @ViewChildren("insideSurveyComponent")
  protected surveyComponents: QueryList<any>;

  @ViewChild("viewerLayer") viewerLayerRef: ElementRef<HTMLDivElement>;
  @ViewChild("camera") camera: CameraComponent;
  @ViewChild("cameraLayer") cameraLayerRef: ElementRef<HTMLDivElement>;

  editorLayer: HTMLDivElement;
  viewerLayer: HTMLDivElement;
  cameraLayer: HTMLDivElement;

  fullScreenOpened: boolean;

  constructor(
    readonly route: ActivatedRoute,
    readonly service: InsideSurveyService,
    protected tokenService: TokenService,
    protected toastService: ToastService,
    protected savingStateService: SavingStateService,
    protected deviceCheckService: DeviceCheckService,
    private formValidationService: FormValidationService,
    protected sessionService: SessionService,
    private buildingService: BuildingService,
    private permissionsService: PermissionsService
  ) {
    super(
      tokenService,
      savingStateService,
      toastService,
      deviceCheckService,
      sessionService
    );
  }

  survey: InsideSurvey;
  surveyStatusEnum = SurveyStatus;
  tabConfig = {
    activeTabId: magicStrings.INSIDE_SURVEY_ADDRESS_INFO,
    destroyOnHide: false,
    tabs: {
      valRemarks: {
        id: magicStrings.VAL_REMARKS,
        disabled: false,
        readonly: true,
        index: 0,
      },
      addressInfo: {
        id: magicStrings.INSIDE_SURVEY_ADDRESS_INFO,
        disabled: false,
        readonly: false,
        index: 1,
      },
      photo: {
        id: magicStrings.INSIDE_SURVEY_PHOTO,
        disabled: false,
        readonly: false,
        index: 2,
      },
      outsideTechnicalInfo: {
        id: magicStrings.INSIDE_SURVEY_OUTSIDE_TECHNICAL_INFO,
        disabled: false,
        readonly: false,
        index: 3,
      },
      insideTechnicalInfo: {
        id: magicStrings.INSIDE_SURVEY_INSIDE_TECHNICAL_INFO,
        disabled: false,
        readonly: false,
        index: 4,
      },
      cablingProposal: {
        id: magicStrings.INSIDE_SURVEY_CABLING_PROPOSAL,
        disabled: false,
        readonly: false,
        index: 5,
      },
      outsideUnits: {
        id: magicStrings.INSIDE_SURVEY_OUTSIDE_UNITS,
        disabled: false,
        readonly: false,
        index: 6,
      },
      insideUnits: {
        id: magicStrings.INSIDE_SURVEY_INSIDE_UNITS,
        disabled: false,
        readonly: false,
        index: 7,
      },
      contactInfo: {
        id: magicStrings.INSIDE_SURVEY_CONTACT_INFO,
        disabled: false,
        readonly: false,
        index: 8,
      },
      validate: {
        id: magicStrings.INSIDE_SURVEY_VALIDATE,
        disabled: false,
        readonly: true,
        index: 9,
      },
    },
  };

  get address() {
    return this.survey.buildings?.[0]?.getMainAddress() || null;
  }
  get buildingKey() {
    return this.survey.buildings?.[0]?.lomKey || null;
  }
  get showValidationRemarksTab(): boolean {
    return (
      !!_.get(this.survey, "validatorRemark", "") &&
      this.survey.surveyStatus === SurveyStatus.InsideOnGoing
    );
  }
  get validateTabVisible(): boolean {
    if (!this.survey) return false;

    if (!this.permissionsService.allowed(PermissionsEnum.ValidateSurvey))
      return false;

    const status = this.survey.surveyStatus;
    const passingStatuses = [
      SurveyStatus.InsideWaitingForValidation,
      SurveyStatus.InsideDone,
    ];
    return _.includes(passingStatuses, status);
  }

  ngOnInit() {
    this.setAuth();

    if (!this.showValidationRemarksTab)
      this.tabConfig.activeTabId = magicStrings.INSIDE_SURVEY_ADDRESS_INFO;

    this.setBlockAddress();
    this.names = [
      this.survey?.insideEngineer?.firstName || "",
      this.survey?.insideEngineer?.lastName || "",
    ];
    this.setInitials(this.names);

    this.formValidationService.clearForm();
    this.refreshValidateTab();
    this.navActions = this.initNavActions();
  }

  ngAfterViewInit(): void {
    this.viewerLayer = this.viewerLayerRef.nativeElement;
    this.cameraLayer = this.cameraLayerRef.nativeElement;

    if (this.survey.buildings?.[0])
      this.sessionService.setLocation(this.survey.buildings[0]);
  }

  getErrors(tab: string): boolean {
    return this.formValidationService.getErrors(tab);
  }

  async canDeactivate(): Promise<boolean> {
    if (this.isAuthCanWrite) {
      await this.saveCurrentTab();
      return this.savingStateService.canDeactivateCheck();
    } else {
      return true;
    }
  }

  deactivate() {
    this.savingStateService.discardChanges();
  }

  navActions: NavAction[] = [];

  initNavActions(): NavAction[] {
    let list: NavAction[] = [];
    if (
      (this.permissionsService.allowed(PermissionsEnum.EditSurvey) &&
        this.survey.surveyStatus === SurveyStatus.InsideOnGoing) ||
      (this.permissionsService.allowed(PermissionsEnum.ValidateSurvey) &&
        this.survey.surveyStatus === SurveyStatus.InsideWaitingForValidation)
    )
      list.unshift(
        {
          icon: "exit_to_app",
          onClick: () => this.onSaveAndGoBackButtonClick(),
          tooltip: "Save & exit",
        },
        {
          icon: "save",
          onClick: () => this.onSaveButtonClick(),
          tooltip: "Save",
        }
      );
    if (
      this.permissionsService.allowed(PermissionsEnum.EditSurvey) &&
      (this.survey.surveyStatus === SurveyStatus.InsideOnGoing ||
        this.survey.surveyStatus === SurveyStatus.InsidePending)
    )
      list.push({
        icon: "done",
        onClick: () => this.completeSurvey(),
        style: "background: #5aea99; color: white",
        tooltip: "Submit",
      });

    if (this.permissionsService.allowed(PermissionsEnum.SurveyHistory))
      list.unshift({
        icon: "history",
        onClick: () => this.generateHistoryExport(),
        tooltip: "History Export",
      });
    return list;
  }

  async onSaveAndGoBackButtonClick() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@validation.photo.nointernet:No internet`,
        $localize`:@@validation.photo.nointernetsave:Saving changes is not possible while you're offline.`
      );
      return;
    }

    await this.saveCurrentTab();

    await this.savingStateService.saveAll(true).toPromise();
  }

  async onSaveButtonClick() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@validation.photo.nointernet:No internet`,
        $localize`:@@validation.photo.nointernetsave:Saving changes is not possible while you're offline.`
      );
      return;
    }

    await this.saveCurrentTab();

    await this.savingStateService.saveAll(false).toPromise();
  }

  private setAuth() {
    switch (this.survey.surveyStatus) {
      case SurveyStatus.InsidePending:
      case SurveyStatus.InsideOnGoing:
        this.isAuthCanWrite = this.permissionsService.allowed(
          PermissionsEnum.EditSurvey
        );
        break;
      case SurveyStatus.InsideWaitingForValidation:
        this.isAuthCanWrite = this.permissionsService.allowed(
          PermissionsEnum.ValidateSurvey
        );
        break;
      default:
        this.isAuthCanWrite = false;
        break;
    }
  }

  async completeSurvey() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@validation.photo.nointernet:No internet`,
        $localize`:@@validation.photo.nointernetcompletingsurvey:Completing survey is not possible while you're offline.`
      );
      return;
    }
    await this.saveCurrentTab();

    const error: ValidationError = this.surveyComponents[RESULTS].map(
      (x) => x && x.getValidationError(true)
    ).find((x) => !!x);

    if (error) {
      this.toastService.showErrorToast(
        `${error.title}: ${error.sender}`,
        error.message
      );
      return;
    }

    this.surveyComponents
      .find(
        (c) => c.tabId == this.tabConfig.tabs[this.tabConfig.activeTabId].id
      )
      .save(true);

    return this.savingStateService
      .saveAll(true, this.service.complete(this.survey.id))
      .pipe(first())
      .toPromise();
  }

  toggleLayer(layer: HTMLDivElement) {
    layer.classList.toggle("hidden");
  }

  closeViewer() {
    this.viewerLayer.style.backgroundImage = null;
    this.toggleLayer(this.viewerLayer);
  }

  generateHistoryExport() {
    this.buildingService
      .exportBuildingActionCsv({
        buildingIds: this.survey.buildings.map((b) => b.id),
      })
      .pipe(first())
      .subscribe(
        (res) => {
          this.downloadFile(res);
          this.toastService.showSuccesToast(
            $localize`:@@History.csv.triggered:csv has been triggered`,
            $localize`:@@general.csv:csv`
          );
        },
        (err) => {
          this.toastService.showErrorToast(
            $localize`:@@validation.somethingwhentwrong:Something went wrong!`,
            $localize`:@@admin.csv.somethingswrongelaborated:Something went wrong while generating the csv!`
          );
        }
      );
  }

  downloadFile(response: {
    fileName: string;
    fileType: string;
    data: ArrayBuffer;
  }) {
    let blob = new Blob([response.data], { type: response.fileType });

    const data = window.URL.createObjectURL(blob);
    var link = document.createElement("a");
    link.href = data;
    link.download = response.fileName;
    link.click();

    setTimeout(function () {
      // for Firefox it is necessary to delay revoking the ObjectURL
      window.URL.revokeObjectURL(data);
    }, 100);
  }
}
